import React, { useState } from 'react'
import {
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'
import { TermsModal } from './TermsModal'

interface RegisterData {
  email: string
  name: string
  companyName: string
  agreeToTerms: boolean
  sendTermsEmail: boolean
}

interface RegisterFormProps {
  onSuccess: () => void
  onSwitchToLogin: () => void
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { registerNewTenant, isLoading: authLoading } = useAuth()
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    name: '',
    companyName: '',
    agreeToTerms: false,
    sendTermsEmail: false,
  })
  const [error, setError] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [termsModalOpen, setTermsModalOpen] = useState(false)

  const loading = authLoading

  const handleInputChange =
    (field: keyof RegisterData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }))

      // フィールド変更時にエラーをクリア
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: '' }))
      }
      if (error) {
        setError('')
      }
    }

  const handleTermsClick = (event: React.MouseEvent) => {
    event.preventDefault()
    setTermsModalOpen(true)
  }

  const handleCheckboxClick = (event: React.MouseEvent) => {
    // チェックボックスのデフォルト動作を防止し、モーダルを開く
    event.preventDefault()
    if (!formData.agreeToTerms) {
      setTermsModalOpen(true)
    } else {
      // 既に同意済みの場合はチェックを外せる
      setFormData((prev) => ({ ...prev, agreeToTerms: false }))
    }
  }

  const handleTermsAgree = (sendEmail: boolean) => {
    setFormData((prev) => ({ ...prev, agreeToTerms: true, sendTermsEmail: sendEmail }))
    setFieldErrors((prev) => ({ ...prev, agreeToTerms: '' }))
    setTermsModalOpen(false)
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // 必須フィールドチェック
    if (!formData.email.trim()) {
      errors.email = 'メールアドレスを入力してください'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '有効なメールアドレスを入力してください'
    }

    if (!formData.name.trim()) {
      errors.name = '管理者名を入力してください'
    }

    if (!formData.companyName.trim()) {
      errors.companyName = '企業名を入力してください'
    }

    if (!formData.agreeToTerms) {
      errors.agreeToTerms = '利用規約に同意してください'
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validateForm()) {
      return
    }

    setError('')

    try {
      const registrationData = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        companyName: formData.companyName.trim(),
        sendTermsEmail: formData.sendTermsEmail,
      }

      await registerNewTenant(registrationData)
      onSuccess()
    } catch (error: any) {
      console.error('登録エラー:', error)

      if (error.response?.data?.error) {
        setError(error.response.data.error)
      } else if (error.response?.data?.details?.length > 0) {
        setError(error.response.data.details[0].msg)
      } else {
        setError('登録に失敗しました。時間をおいて再度お試しください。')
      }
    }
  }

  return (
    <Box sx={{ minWidth: '100%' }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, md: 4 },
          maxWidth: 500,
          mx: 'auto',
        }}
      >
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* ヘッダー */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" component="h1" gutterBottom>
                新規企業登録
              </Typography>
              <Typography variant="body2" color="text.secondary">
                企業管理者として新規登録します
              </Typography>
            </Box>

            {/* エラー表示 */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* 企業情報 */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                企業情報
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="企業名"
                  value={formData.companyName}
                  onChange={handleInputChange('companyName')}
                  error={Boolean(fieldErrors.companyName)}
                  helperText={fieldErrors.companyName}
                  required
                  disabled={loading}
                />
              </Stack>
            </Box>

            {/* 管理者情報 */}
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                管理者情報
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="管理者名"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                  required
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  type="email"
                  label="メールアドレス"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={Boolean(fieldErrors.email)}
                  helperText={fieldErrors.email}
                  required
                  disabled={loading}
                  autoComplete="username"
                />
              </Stack>
            </Box>

            {/* 利用規約 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.agreeToTerms}
                  onClick={handleCheckboxClick}
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2">
                  <Link
                    component="button"
                    type="button"
                    onClick={handleTermsClick}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    利用規約
                  </Link>
                  および
                  <Link
                    href="https://www.garapon-studio.com/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    プライバシーポリシー
                  </Link>
                  に同意します
                </Typography>
              }
              sx={{
                color: fieldErrors.agreeToTerms ? 'error.main' : 'text.primary',
                '.MuiFormControlLabel-asterisk': {
                  color: 'error.main',
                },
              }}
            />
            {fieldErrors.agreeToTerms && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {fieldErrors.agreeToTerms}
              </Typography>
            )}

            {/* 利用規約モーダル */}
            <TermsModal
              open={termsModalOpen}
              onClose={() => setTermsModalOpen(false)}
              onAgree={handleTermsAgree}
            />

            {/* 送信ボタン */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : '企業登録'}
            </Button>

            {/* ログイン画面への切り替え */}
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                既にアカウントをお持ちですか？
                <Button variant="text" onClick={onSwitchToLogin} disabled={loading} sx={{ ml: 1 }}>
                  ログイン
                </Button>
              </Typography>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}
